export class StopSearchResourceError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "StopSearchResourceError";
    this.code = code;
  }
}

const isAbortError = (error) => error?.name === "AbortError";

export const isStopSearchResult = (result) =>
  result &&
  typeof result === "object" &&
  typeof result.stop_id === "string" &&
  result.stop_id.length > 0 &&
  typeof result.stop_name === "string" &&
  result.stop_name.length > 0;

export async function fetchJsonArray(url, options) {
  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      throw new StopSearchResourceError("http", `Search request failed (${response.status})`);
    }

    let payload;

    try {
      payload = await response.json();
    } catch {
      throw new StopSearchResourceError("malformed", "Search response was not JSON");
    }

    if (!Array.isArray(payload)) {
      throw new StopSearchResourceError("malformed", "Search response was not an array");
    }

    return payload;
  } catch (error) {
    if (error instanceof StopSearchResourceError) {
      throw error;
    }

    if (isAbortError(error)) {
      throw new StopSearchResourceError("aborted", "Search request was cancelled");
    }

    throw new StopSearchResourceError("network", "Search request failed");
  }
}

export async function fetchStopSearchResults(url, options) {
  const results = await fetchJsonArray(url, options);

  if (!results.every(isStopSearchResult)) {
    throw new StopSearchResourceError("malformed", "Search response contained an invalid stop");
  }

  return results;
}

export function getStopSearchResourceState({
  data,
  error,
  isLoading = false,
  isValidating = false,
  isPending = false,
}) {
  const hasData = Array.isArray(data);

  if (error?.code === "aborted" || isAbortError(error)) {
    if (hasData) return data.length > 0 ? "ready" : "empty";
    return isLoading || isValidating ? "loading" : "idle";
  }

  if (error) {
    return hasData ? "refresh-error" : "unavailable";
  }

  if (isPending && !hasData) {
    return "loading";
  }

  if (isLoading || (isValidating && !hasData)) {
    return "loading";
  }

  if (hasData) {
    return data.length > 0 ? "ready" : "empty";
  }

  return "idle";
}
